import React from "react"
import {useTranslation} from "react-i18next"
import {ArrowRightIcon, CircleStackIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {useKlaraUpdateCenterQuery} from "../../../queries/klaraDashboard.ts"
import {EMPTY_ARRAY, getFullName} from "../../../utils/index.ts"
import {KlaraFiltersContext} from "../filters.tsx"
import {getPeriodRange} from "../period.ts"
import {entryInitials, formatSessionTime, summarizeEntry, TKlaraFeedEntry} from "../sessions.ts"
import {TDashboardClock, useDashboardClock} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {CARD_CLASS_NAME, CARD_TITLE_CLASS_NAME} from "./cardStyles.ts"
import {PersonChip} from "./PersonChip.tsx"

const LATEST_UPDATES_LIMIT = 20
const VISIBLE_ORGANISATIONS = 2

export const LatestUpdatesCard: React.FC = () => {
  const {t} = useTranslation()
  const {salesPersonExternalId, openDrawer} = KlaraFiltersContext.useContext()
  const [period] = useKlaraPeriod()
  // The picker computes preset dates on the browser's calendar.
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const titleId = React.useId()

  // Same key as the Update center drawer without a search or a jump, so both read one cached feed.
  const {data, isPending, isError, isFetching, isFetchingNextPage, isPlaceholderData, refetch} =
    useKlaraUpdateCenterQuery({salesPersonExternalId, ...range})
  const entries = (data?.pages[0]?.data.update_center_entries ?? EMPTY_ARRAY).slice(0, LATEST_UPDATES_LIMIT)
  const clock = useDashboardClock()

  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(CARD_CLASS_NAME, "flex min-w-0 flex-[1_1_340px] flex-col px-6 py-5")}
    >
      <div className={"mb-2.5 flex items-center justify-between gap-3"}>
        <div className={"flex min-w-0 flex-wrap items-center gap-2.5"}>
          <h2 id={titleId} className={CARD_TITLE_CLASS_NAME}>
            {t("KlaraDashboard_LatestUpdates_Title")}
          </h2>
          <PersonChip />
        </div>
        <button
          type={"button"}
          onClick={() => openDrawer({kind: "updates"})}
          className={"inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-cr-blue hover:underline"}
        >
          {t("KlaraDashboard_LatestUpdates_ViewAll")}
          <ArrowRightIcon className={"size-3.5"} aria-hidden={"true"} />
        </button>
      </div>
      <CardState
        // The drawer pages through the same feed: its paging state and errors stay out of the card while page 1
        // has data.
        query={{isPending, isError: isError && !data, isFetching: isFetching && !isFetchingNextPage, refetch}}
        skeletonClassName={"h-64"}
      >
        {entries.length === 0 ? (
          <p className={"py-8 text-center text-sm text-cr-slate-60"}>{t("KlaraDashboard_LatestUpdates_Empty")}</p>
        ) : (
          <ul className={"-mr-3 flex scrollbar-slim max-h-[300px] flex-col overflow-y-auto pr-3"}>
            {entries.map(entry => (
              <li key={entry.id}>
                <LatestUpdateRow
                  entry={entry}
                  clock={clock}
                  // Rows from the previous scope stay visible while the new one loads, but they cannot be opened
                  // in a drawer that already shows the new scope.
                  disabled={isPlaceholderData}
                  onClick={() => openDrawer({kind: "updates", entryId: entry.id})}
                />
              </li>
            ))}
          </ul>
        )}
      </CardState>
    </section>
  )
}

const LatestUpdateRow: React.FC<{
  entry: TKlaraFeedEntry
  clock: TDashboardClock
  disabled: boolean
  onClick: () => void
}> = ({entry, clock, disabled, onClick}) => {
  const {t} = useTranslation()
  const summary = summarizeEntry(entry)
  const name = summary.isCrm
    ? t("KlaraDashboard_UpdateCenter_Channel_CrmSystem")
    : getFullName(entry.sales_person, t("KlaraDashboard_UpdateCenter_UnknownSalesPerson"))
  const hiddenOrganisations = summary.organisations.length - VISIBLE_ORGANISATIONS

  return (
    <button
      type={"button"}
      onClick={onClick}
      disabled={disabled}
      className={
        "-mx-2 grid w-[calc(100%+1rem)] grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg border-t border-cr-slate-6 p-2 text-left enabled:hover:bg-cr-blue-super-light disabled:cursor-default"
      }
    >
      {summary.isCrm ? (
        <span className={"flex size-7 items-center justify-center rounded-full bg-cr-slate-6"}>
          <CircleStackIcon className={"size-3.5 text-cr-slate-60"} aria-hidden={"true"} />
        </span>
      ) : (
        <span
          aria-hidden={"true"}
          className={
            "flex size-7 items-center justify-center rounded-full bg-cr-blue-light text-[10px] font-bold text-cr-blue-dark"
          }
        >
          {entryInitials(entry)}
        </span>
      )}
      <span className={"min-w-0"}>
        <span className={"block truncate text-[12.5px] text-cr-slate-90"}>
          <strong className={"font-bold"}>{name}</strong>
          {" · "}
          {t("KlaraDashboard_UpdateCenter_Deals", {count: summary.dealsCount})}
          {" · "}
          {t("KlaraDashboard_UpdateCenter_Activities", {count: summary.activitiesCount})}
        </span>
        <span className={"mt-px flex min-w-0 items-center gap-1.5"}>
          {summary.hasWon ? (
            <span data-testid={"badge"} className={twMerge(FLAG_CLASS_NAME, "bg-cr-green-pale text-cr-green-dark")}>
              {t("KlaraDashboard_UpdateCenter_Won")}
            </span>
          ) : (
            summary.stageMoves > 0 && (
              <span data-testid={"badge"} className={twMerge(FLAG_CLASS_NAME, "bg-cr-blue-light text-cr-blue-dark")}>
                {t("KlaraDashboard_LatestUpdates_StageMoves", {count: summary.stageMoves})}
              </span>
            )
          )}
          <span className={"min-w-0 truncate text-[11px] text-cr-slate-60"}>
            {summary.organisations.slice(0, VISIBLE_ORGANISATIONS).join(", ")}
            {hiddenOrganisations > 0 && ` +${hiddenOrganisations}`}
          </span>
        </span>
      </span>
      <span className={"text-[11px] whitespace-nowrap text-cr-slate-60"}>
        {formatSessionTime(entry.tracked_at, {now: clock.asOf, timeZone: clock.timeZone})}
      </span>
    </button>
  )
}

const FLAG_CLASS_NAME = "shrink-0 rounded-full px-[7px] py-px text-[10.5px] font-bold whitespace-nowrap"
